import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, vModelCheckbox as _vModelCheckbox, normalizeClass as _normalizeClass, withDirectives as _withDirectives, vShow as _vShow, withModifiers as _withModifiers, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "checkout-page" }
const _hoisted_2 = {
  key: 0,
  class: "loading"
}
const _hoisted_3 = {
  key: 1,
  class: "checkout-container"
}
const _hoisted_4 = { class: "checkout-header" }
const _hoisted_5 = { class: "plan-summary" }
const _hoisted_6 = { class: "customer-section" }
const _hoisted_7 = { class: "terms" }
const _hoisted_8 = ["onUpdate:modelValue"]
const _hoisted_9 = { class: "checkbox-text" }
const _hoisted_10 = ["href"]
const _hoisted_11 = {
  key: 0,
  class: "terms-hint"
}
const _hoisted_12 = { class: "payment-section" }
const _hoisted_13 = ["disabled"]

import store from "@/store";
import { computed, nextTick, onMounted, reactive, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import {
  loadStripe,
  type Stripe,
  type StripeElements,
  type StripePaymentElement,
} from "@stripe/stripe-js";

import { toast } from "@/common/utils/toast";
import { maskCPF, maskCNPJ } from "@/common/utils/mask";

import { planGet } from "@/common/api/rest/plan";
import { checkoutPlan } from "@/common/api/rest/payment/checkout";
import { userAccountByMember } from "@/common/api/rest/user";

import type { PlanType } from "@/common/types/plan/plan";

import { DefaultTheme } from "@/common/themes";
import { baseInput, baseSelect } from "@/common/components";


export default /*@__PURE__*/_defineComponent({
  __name: '[plan]',
  setup(__props) {

const { t } = useI18n();
const route = useRoute();
const router = useRouter();

const account = ref<any>(null);
const plan = ref<PlanType | null>(null);
const isLoading = ref(true);
const isProcessing = ref(false);

const terms = ref<any[]>([]);
const error = ref<{ [key: string]: string }>({});

const stripe = ref<Stripe | null>(null);
const elements = ref<StripeElements | null>(null);
const paymentElement = ref<StripePaymentElement | null>(null);
const clientSecret = ref("");

const DEFAULT_PAYLOAD = {
  name: "",
  email: "",
  doc_type: "br_cpf",
  doc_number: "",
};

const payload = ref<any>({ ...DEFAULT_PAYLOAD });
const frequency = ref("MONTHLY");

const docTypeOptions = computed(() => [
  { option: "CPF", value: "br_cpf" },
  { option: "CNPJ", value: "br_cnpj" },
]);

const frequencyOptions = computed(() => ({
  MONTHLY: "Mensal",
  SEMESTER: "Semestral",
  YEARLY: "Anual",
}));

const currencyOptions = computed(() => ({
  BRL: "R$",
  USD: "$",
  EUR: "€",
}));

function documentMask(value: any) {
  if (payload.value.doc_type === "br_cpf") return maskCPF(value);

  if (payload.value.doc_type === "br_cnpj") return maskCNPJ(value);

  return value;
}

function changeType(type: any) {
  payload.value.doc_type = type;
  payload.value.doc_number = documentMask(payload.value.doc_number);
}

async function initStripe() {
  const pk = process.env.VUE_APP_STRIPE_PUBLIC;
  if (!pk) {
    toast({
      message: "Erro ao carregar chave pública do Stripe",
      type: "error",
    });
    return;
  }

  stripe.value = await loadStripe(pk);

  if (!stripe.value) {
    toast({ message: "Erro ao carregar instância do Stripe", type: "error" });
  }
}

async function fetchAccount() {
  try {
    const response = await userAccountByMember(store.getters.user?.member_id);
    if (response.success) {
      account.value = response.data;
    } else {
      toast({ message: response.message, type: "error" });
    }
  } catch (error) {
    toast({ message: t("error.default"), type: "error" });
  }
}

async function fetchPlan() {
  const planId = route.params.plan as string;
  if (!planId) {
    toast({ message: t("plan.errors.plan_not_found"), type: "error" });
    router.push("/plans");
    return;
  }

  try {
    const accountId = account.value?.account_id;
    const response = await planGet(accountId, planId);
    if (response.success) {
      plan.value = response.data;
      terms.value = (response.data?.terms || []).map((item: any) => ({
        ...item,
        accepted: false,
        url: `https://www.segform.com.br/pt/term/${item.code.toLowerCase()}`,
      }));
    } else {
      toast({ message: response.message, type: "error" });
      router.push("/plans");
    }
  } catch (error) {
    toast({ message: t("error.default"), type: "error" });
  }
}

/**
 * Desmonta o Payment Element e limpa o clientSecret,
 * voltando o formulário para o estado "Continuar".
 */
function resetPayment() {
  if (paymentElement.value) {
    try {
      paymentElement.value.unmount();
    } catch (_) {
      /* unmount pode falhar se já foi desmontado */
    }
    paymentElement.value = null;
  }
  elements.value = null;
  clientSecret.value = "";
}

/**
 * Se o usuário desmarcar qualquer termo depois que o cartão já foi liberado,
 * removemos o cartão e obrigamos ele a marcar tudo de novo.
 */
watch(
  terms,
  () => {
    if (!clientSecret.value) return;

    const allAccepted = terms.value.every((item) => item.accepted);
    if (!allAccepted) {
      resetPayment();
    }
  },
  { deep: true },
);

async function preparePayment() {
  if (!plan.value || !stripe.value) {
    toast({ message: "Stripe não inicializado corretamente", type: "error" });
    return false;
  }

  try {
    const price = plan.value.prices.find(
      (item) => item.frequency === frequency.value,
    );

    // 1. inicializa o checkout do plano na API do Stripe
    const checkoutResponse = await checkoutPlan(account.value.account_id, {
      member_id: store.getters.user.member_id,
      plan_id: plan.value.plan_id,
      price_id: price.price_id,
      name: payload.value.name,
      email: payload.value.email,
      doc_type: payload.value.doc_type,
      doc_number: payload.value.doc_number,
    });

    if (!checkoutResponse.success) {
      throw new Error(checkoutResponse.message);
    }

    clientSecret.value = checkoutResponse.data.client_secret;

    if (paymentElement.value) {
      paymentElement.value.unmount();
      paymentElement.value = null;
    }

    // Instancia Elements passando o clientSecret obrigatório
    elements.value = stripe.value.elements({
      clientSecret: clientSecret.value,
      appearance: { theme: "stripe" },
    });

    await nextTick();
    paymentElement.value = elements.value.create("payment");
    paymentElement.value.mount("#payment-element");

    return true;
  } catch (error: any) {
    toast({ message: error.message || t("error.default"), type: "error" });
    return false;
  }
}

async function confirmPayment() {
  if (!stripe.value || !elements.value || !clientSecret.value) return;

  try {
    isProcessing.value = true;

    const { error } = await stripe.value.confirmPayment({
      elements: elements.value,
      confirmParams: {
        return_url: `${window.location.origin}/signs`,
        payment_method_data: {
          billing_details: {
            name: payload.value.name,
            email: payload.value.email,
          },
        },
      },
    });

    if (error) {
      toast({ message: error.message || t("error.default"), type: "error" });
    }
  } catch (error: any) {
    toast({ message: error.message || t("error.default"), type: "error" });
  } finally {
    isProcessing.value = false;
  }
}

function valid() {
  return (
    payload.value.name &&
    payload.value.email &&
    payload.value.doc_number &&
    terms.value.every((item) => item.accepted)
  );
}

async function handleSubmit() {
  if (!valid()) {
    toast({ message: "Campos obrigatórios não preenchidos", type: "error" });
    return;
  }

  if (!clientSecret.value) {
    isProcessing.value = true;
    await preparePayment();
    isProcessing.value = false;
  } else {
    // 2ª etapa: Confirma o pagamento enviado no Element
    await confirmPayment();
  }
}

function getCurrency(item: PlanType) {
  const price = item.prices.find((p) => p.frequency === frequency.value);

  if (price) {
    return currencyOptions.value[price.currency];
  }

  return " -- ";
}

function getPrice(item: PlanType) {
  const price = item.prices.find((p) => p.frequency === frequency.value);

  if (price) {
    return (price.price / 100).toLocaleString("pt-BR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  return " -- ";
}

function getDescription(item: PlanType) {
  const price = item.prices.find((p) => p.frequency === frequency.value);

  if (price) {
    return price.description;
  }

  return " -- ";
}

function goBack() {
  router.push("/plans");
}

onMounted(async () => {
  isLoading.value = true;
  await fetchAccount();
  await initStripe();
  await fetchPlan();
  isLoading.value = false;

  frequency.value = route.query.frequency as string;
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_unref(DefaultTheme), {
    "menu-active": "plans",
    "account-status": account.value?.status
  }, {
    default: _withCtx(() => [
      _createElementVNode("section", _hoisted_1, [
        (isLoading.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_2, _toDisplayString(_unref(t)("common.loading")), 1))
          : (plan.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                _createElementVNode("header", _hoisted_4, [
                  _createElementVNode("h1", null, _toDisplayString(_unref(t)("checkout.labels.title")), 1),
                  _createElementVNode("div", _hoisted_5, [
                    _createElementVNode("span", null, _toDisplayString(plan.value.name), 1),
                    _createElementVNode("strong", null, _toDisplayString(getCurrency(plan.value)) + " " + _toDisplayString(getPrice(plan.value)) + " / " + _toDisplayString(frequencyOptions.value[frequency.value]), 1)
                  ])
                ]),
                _createElementVNode("form", {
                  onSubmit: _withModifiers(handleSubmit, ["prevent"]),
                  class: "checkout-form"
                }, [
                  _createElementVNode("section", _hoisted_6, [
                    _createElementVNode("h2", null, _toDisplayString(_unref(t)("checkout.labels.customer")), 1),
                    _createVNode(_unref(baseInput), {
                      modelValue: payload.value.name,
                      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((payload.value.name) = $event)),
                      value: payload.value.name
                    }, {
                      label: _withCtx(() => [
                        _createTextVNode(_toDisplayString(_unref(t)("common.labels.name")) + " * ", 1)
                      ]),
                      _: 1
                    }, 8, ["modelValue", "value"]),
                    _createVNode(_unref(baseInput), {
                      modelValue: payload.value.email,
                      "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((payload.value.email) = $event)),
                      value: payload.value.email
                    }, {
                      label: _withCtx(() => [
                        _createTextVNode(_toDisplayString(_unref(t)("common.labels.email")) + " * ", 1)
                      ]),
                      _: 1
                    }, 8, ["modelValue", "value"]),
                    _createVNode(_unref(baseSelect), {
                      modelValue: payload.value.doc_type,
                      "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((payload.value.doc_type) = $event)),
                      value: payload.value.doc_type,
                      options: docTypeOptions.value,
                      "is-empty": false,
                      onChange: changeType
                    }, {
                      label: _withCtx(() => [
                        _createTextVNode(_toDisplayString(_unref(t)("common.labels.document_type")) + " * ", 1)
                      ]),
                      _: 1
                    }, 8, ["modelValue", "value", "options"]),
                    _createVNode(_unref(baseInput), {
                      modelValue: payload.value.doc_number,
                      "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((payload.value.doc_number) = $event)),
                      value: payload.value.doc_number,
                      mask: documentMask
                    }, {
                      label: _withCtx(() => [
                        _createTextVNode(_toDisplayString(_unref(t)("common.labels.number")) + " * ", 1)
                      ]),
                      _: 1
                    }, 8, ["modelValue", "value"])
                  ]),
                  _createElementVNode("div", _hoisted_7, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(terms.value, (term) => {
                      return (_openBlock(), _createElementBlock("label", {
                        key: term.term_id,
                        class: "terms-checkbox"
                      }, [
                        _withDirectives(_createElementVNode("input", {
                          "onUpdate:modelValue": ($event: any) => ((term.accepted) = $event),
                          type: "checkbox",
                          class: _normalizeClass(["checkbox-input", { 'has-error': error.value[term.term_id] }])
                        }, null, 10, _hoisted_8), [
                          [_vModelCheckbox, term.accepted]
                        ]),
                        _createElementVNode("span", _hoisted_9, [
                          _cache[4] || (_cache[4] = _createTextVNode(" Li e concordo com ", -1)),
                          _createElementVNode("a", {
                            href: term.url,
                            target: "_blank"
                          }, _toDisplayString(term.name), 9, _hoisted_10)
                        ])
                      ]))
                    }), 128))
                  ]),
                  (terms.value.length && !terms.value.every((item) => item.accepted) && !clientSecret.value)
                    ? (_openBlock(), _createElementBlock("p", _hoisted_11, " Marque todos os termos para liberar o pagamento. "))
                    : _createCommentVNode("", true),
                  _withDirectives(_createElementVNode("section", _hoisted_12, [
                    _createElementVNode("h2", null, _toDisplayString(_unref(t)("checkout.labels.card")), 1),
                    _cache[5] || (_cache[5] = _createElementVNode("div", {
                      id: "payment-element",
                      class: "payment-element"
                    }, null, -1))
                  ], 512), [
                    [_vShow, clientSecret.value]
                  ]),
                  _createElementVNode("button", {
                    type: "submit",
                    class: "btn btn-primary btn-lg",
                    disabled: isProcessing.value
                  }, _toDisplayString(isProcessing.value
                ? _unref(t)("checkout.labels.processing")
                : clientSecret.value
                  ? _unref(t)("checkout.labels.pay")
                  : _unref(t)("checkout.labels.continue")), 9, _hoisted_13)
                ], 32),
                _createElementVNode("button", {
                  class: "btn back",
                  onClick: goBack
                }, _toDisplayString(_unref(t)("common.labels.back")), 1)
              ]))
            : _createCommentVNode("", true)
      ])
    ]),
    _: 1
  }, 8, ["account-status"]))
}
}

})